import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '@inertiajs/react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

import PrimaryButton from '@/components/Buttons/PrimaryButton';
import FileUploader from '@/components/FileUploader';
import InputError from '@/components/Forms/InputError';
import TextInput from '@/components/Forms/TextInput';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';

interface Record {
    full_name: string;
    email: string;
    phone_number: string;
    admission_person: string;
    enrolled_program: string;
    program_start_date: string;
    comments: string;
    submitted_at: string | null;
    created_at: string | null;
}

interface Props {
    programs: string[];
    record: Record;
    errors?: { [key: string]: string[] };
}

const NoBillingOrderFormContent: React.FC<Props> = ({ programs, record, errors: serverErrors }) => {
    const { t } = useTranslation();
    const [paymentFile, setPaymentFile] = useState<File | null>(null);

    const { data, setData, post, processing, errors } = useForm({
        full_name: record.full_name,
        email: record.email,
        phone_number: record.phone_number,
        admission_person: record.admission_person,
        enrolled_program: record.enrolled_program,
        program_start_date: record.program_start_date,
        payment_receipt: null as File | null,
        comments: record.comments,
    });

    const allErrors = { ...errors, ...serverErrors };

    const daysRemaining = record.created_at
        ? Math.max(0, 7 - Math.floor((Date.now() - new Date(record.created_at).getTime()) / (1000 * 60 * 60 * 24)))
        : 7;
    const isUrgent = daysRemaining <= 0;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post('/no-billing-order/', {
            forceFormData: true,
        });
    };

    const handleFileSelect = (file: File | null) => {
        setPaymentFile(file);
        setData('payment_receipt', file);
    };

    if (record.submitted_at) {
        return (
            <AppLayout>
                <div className="mx-auto max-w-2xl px-4 py-12">
                    <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
                        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
                        <h2 className="mb-2 text-xl font-semibold text-green-800">
                            {t('noBillingOrder.submittedTitle')}
                        </h2>
                        <p className="text-green-700">
                            {t('noBillingOrder.submittedMessage')}
                        </p>
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <div className="mx-auto max-w-2xl px-4 py-8">
                <PageTitle>{t('noBillingOrder.pageTitle')}</PageTitle>

                <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4">
                    <div className="flex gap-3">
                        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-500" />
                        <p className="text-sm text-orange-800">
                            {isUrgent
                                ? t('noBillingOrder.introTextUrgent')
                                : t('noBillingOrder.introText', { days: daysRemaining })
                            }
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 rounded-xl border border-white/50 bg-white/30 p-6 backdrop-blur-sm dark:border-white/10 dark:bg-white/10 [box-shadow:0_3px_10px_rgba(0,0,0,0.22),0_1px_4px_rgba(0,0,0,0.15)]">
                    <TextInput
                        label={t('noBillingOrder.fullName')}
                        name="full_name"
                        value={data.full_name}
                        onChange={(val) => setData('full_name', val)}
                        required
                        error={allErrors.full_name}
                    />

                    <TextInput
                        label={t('noBillingOrder.email')}
                        name="email"
                        type="email"
                        value={data.email}
                        onChange={(val) => setData('email', val)}
                        required
                        error={allErrors.email}
                    />

                    <TextInput
                        label={t('noBillingOrder.phoneNumber')}
                        name="phone_number"
                        value={data.phone_number}
                        onChange={(val) => setData('phone_number', val)}
                        required
                        helpText={t('noBillingOrder.phoneHelp')}
                        error={allErrors.phone_number}
                    />

                    <TextInput
                        label={t('noBillingOrder.admissionPerson')}
                        name="admission_person"
                        value={data.admission_person}
                        onChange={(val) => setData('admission_person', val)}
                        error={allErrors.admission_person}
                    />

                    <div>
                        <label htmlFor="enrolled_program" className="mb-1 block text-sm font-medium text-gray-500">
                            {t('noBillingOrder.enrolledProgram')}
                            <span className="ml-1 text-red-500">*</span>
                        </label>
                        <select
                            id="enrolled_program"
                            name="enrolled_program"
                            value={data.enrolled_program}
                            onChange={(e) => setData('enrolled_program', e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        >
                            <option value="">{t('noBillingOrder.selectProgram')}</option>
                            {programs.map((program) => (
                                <option key={program} value={program}>
                                    {program}
                                </option>
                            ))}
                        </select>
                        <InputError error={allErrors.enrolled_program} />
                    </div>

                    <TextInput
                        label={t('noBillingOrder.programStartDate')}
                        name="program_start_date"
                        type="date"
                        value={data.program_start_date}
                        onChange={(val) => setData('program_start_date', val)}
                        required
                        error={allErrors.program_start_date}
                    />

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-500">
                            {t('noBillingOrder.paymentReceipt')}
                        </label>
                        <FileUploader
                            onFileSelect={handleFileSelect}
                            selectedFile={paymentFile}
                            accept="image/*,.pdf"
                        />
                        <InputError error={allErrors.payment_receipt} />
                    </div>

                    <div>
                        <label htmlFor="comments" className="mb-1 block text-sm font-medium text-gray-500">
                            {t('noBillingOrder.comments')}
                        </label>
                        <textarea
                            id="comments"
                            name="comments"
                            value={data.comments}
                            onChange={(e) => setData('comments', e.target.value)}
                            rows={3}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                        <InputError error={allErrors.comments} />
                    </div>

                    <PrimaryButton
                        type="submit"
                        disabled={processing}
                        loading={processing}
                        size="full-width"
                    >
                        {processing ? t('noBillingOrder.submitting') : t('noBillingOrder.submit')}
                    </PrimaryButton>
                </form>

                <p className="mt-4 text-center text-xs text-gray-500">
                    {t('noBillingOrder.disclosure')}
                </p>
            </div>
        </AppLayout>
    );
};

const NoBillingOrderForm: React.FC<Props> = (props) => {
    return (
        <ThemeWrapper>
            <NoBillingOrderFormContent {...props} />
        </ThemeWrapper>
    );
};

export default NoBillingOrderForm;
